import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { BlackFullWidthBtn } from '../../../shared/component/headless/button/BtnBody';
import { getBtnName } from '../../../shared/constants/btnNames';
import { useTextTag } from '../../../shared/hooks/useApi/useTextTag';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { getModalMessage } from '../../../shared/constants/modalMessages';
import belowIcon from '../../../asset/icon/below.png';
import { usePageOwnerIdentityStore } from '../../../shared/store/pageOwnerIdentityStore';
import { canShowTextTagList } from '../../../shared/util/visibility/textTagVisibility';

const TextTagSelectModalMobile: React.FC = () => {
  const { clearModal } = useModalStore();
  const pageOwner = usePageOwnerIdentityStore((s) => s.pageOwner);
  const {
    tags,
    selectedTags,
    listOwnerAui,
    status,
    addSelectedTag,
    removeSelectedTag,
  } = useTextTag();
  const canShowTags = canShowTextTagList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    status,
  });
  const visibleTags = canShowTags ? tags : [];
  const visibleSelectedTags = canShowTags ? selectedTags : [];

  const initialSelectedIdSet = useMemo(
    () => new Set(visibleSelectedTags.map((tag) => tag.id)),
    [visibleSelectedTags],
  );
  const [pendingSelectedIdSet, setPendingSelectedIdSet] = useState<Set<number>>(initialSelectedIdSet);

  const toggleTag = (tagId: number) => {
    setPendingSelectedIdSet((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId);
      else next.add(tagId);
      return next;
    });
  };

  const handleApply = () => {
    visibleTags.forEach((tag) => {
      const wasSelected = initialSelectedIdSet.has(tag.id);
      const isSelected = pendingSelectedIdSet.has(tag.id);
      if (wasSelected === isSelected) return;
      if (isSelected) addSelectedTag(tag);
      else removeSelectedTag(tag);
    });
    clearModal(400);
  };

  const handleClose = () => {
    clearModal(400);
  };

  return (
    <Container>
      <TitleWrapper>
        <Title>{getModalMessage('SELECT_TAG_STATION_TITLE')}</Title>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent={true}
          backgroundImg={belowIcon}
          onClick={handleClose}
          aria-label="Close"
        />
      </TitleWrapper>
      <ContentContainer>
        {visibleTags.map((tag) => {
          const isSelected = pendingSelectedIdSet.has(tag.id);
          return (
            <TagItem key={tag.id} $isSelected={isSelected} onClick={() => toggleTag(tag.id)}>
              {tag.name}
            </TagItem>
          );
        })}
      </ContentContainer>
      <HeadlessBtn value={getBtnName('APPLY')} handleClick={handleApply} StyledBtn={ApplyButton} />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin-top: clamp(12px, 2vh, 30px);
  margin-bottom: clamp(20px, 8vh, 30px);
`;

const Title = styled.h2`
  ${({ theme }) => theme.typography.Body_01_1};
  color: ${({ theme }) => theme.colors.color_Gray_02};
`;


const ContentContainer = styled.div`
  max-height: 45vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TagItem = styled.div<{ $isSelected: boolean }>`
  width: 100%;
  padding: 4px 0;
  border-bottom: 1px dotted ${({ $isSelected, theme }) => ($isSelected ? theme.colors.color_Gray_03 : 'transparent')};
  color: ${({ $isSelected, theme }) => ($isSelected ? theme.colors.color_Gray_03 : theme.colors.color_Gray_05)};
  ${({ theme }) => theme.typography.Body_04};
  cursor: pointer;
`;

const ApplyButton = styled(BlackFullWidthBtn)`
  margin: 20px 0px 15px 0px;
`;

export default TextTagSelectModalMobile;
